import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

export default function SearchableSelect({
  value,
  onChange,
  options = [],
  placeholder = "เลือก...",
  searchPlaceholder = "พิมพ์ค้นหา...",
  disabled = false,
  className = "",
  dropdownClassName = "",
  triggerClassName = "",
  triggerStyle,
  renderValue,
  hideArrow = false,
  menuPortalTarget = typeof document !== 'undefined' ? document.body : null,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [highlight, setHighlight] = useState(-1);
  const rootRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const dropdownRef = useRef(null);
  const [dropdownStyle, setDropdownStyle] = useState(null);

  const selected = useMemo(
    () => options.find((o) => String(o.value) === String(value)) || null,
    [options, value]
  );

  const filtered = useMemo(() => {
    if (!query.trim()) return options;
    const q = query.toLowerCase();
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, query]);

  useEffect(() => {
    if (!open) setQuery("");
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open]);

  useEffect(() => {
    const onClick = (e) => {
      const insideTrigger = rootRef.current?.contains(e.target);
      const insideDropdown = dropdownRef.current?.contains(e.target);
      if (!insideTrigger && !insideDropdown) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  useEffect(() => {
    if (!open) {
      setDropdownStyle(null);
      return;
    }
    const DROPDOWN_MAX_H = 260;
    const GAP = 4;
    const update = () => {
      const el = rootRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom - GAP;
      const spaceAbove = rect.top - GAP;
      const openUpward = spaceBelow < DROPDOWN_MAX_H && spaceAbove > spaceBelow;
      setDropdownStyle(
        openUpward
          ? {
              position: "fixed",
              bottom: `${window.innerHeight - rect.top + GAP}px`,
              left: `${rect.left}px`,
              width: `${rect.width}px`,
              zIndex: 100000,
              maxHeight: `${Math.min(DROPDOWN_MAX_H, spaceAbove)}px`,
            }
          : {
              position: "fixed",
              top: `${rect.bottom + GAP}px`,
              left: `${rect.left}px`,
              width: `${rect.width}px`,
              zIndex: 100000,
              maxHeight: `${Math.min(DROPDOWN_MAX_H, spaceBelow)}px`,
            }
      );
    };
    requestAnimationFrame(() => {
      requestAnimationFrame(update);
    });
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [open]);

  const onKeyDown = (e) => {
    if (!open) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, filtered.length - 1));
      scrollIntoView(highlight + 1);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
      scrollIntoView(Math.max(highlight - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (filtered[highlight]) {
        onSelect(filtered[highlight]);
      }
    }
  };

  const scrollIntoView = (idx) => {
    const list = listRef.current;
    if (!list) return;
    const item = list.children[idx];
    if (item) {
      const itemTop = item.offsetTop;
      const itemBottom = itemTop + item.offsetHeight;
      if (itemTop < list.scrollTop) list.scrollTop = itemTop;
      else if (itemBottom > list.scrollTop + list.clientHeight)
        list.scrollTop = itemBottom - list.clientHeight;
    }
  };

  const onSelect = (opt) => {
    onChange?.(opt.value, opt);
    setOpen(false);
  };

  const dropdownElement = open && dropdownStyle ? (
    <div
      ref={dropdownRef}
      style={dropdownStyle}
      className={`${menuPortalTarget ? 'fixed' : 'absolute'} bg-white border rounded-lg shadow-lg overflow-hidden ${dropdownClassName}`}
      onKeyDown={onKeyDown}
    >
      <div className="bg-white sticky top-0 border-b">
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setHighlight(0);
          }}
          placeholder={searchPlaceholder}
          className="w-full border-none px-3 h-[40px] py-2 focus:ring-2 focus:ring-red-300"
          style={{ lineHeight: "20px", fontSize: "14px" }}
        />
      </div>


      <div
        ref={listRef}
        className="overflow-auto"
        style={{ maxHeight: "220px" }}
      >
        {filtered.length === 0 && (
          <div className="px-3 py-2 text-gray-500 text-sm">ไม่พบรายการ</div>
        )}

        {filtered.map((opt, idx) => {
          const active = String(opt.value) === String(value);
          const hl = idx === highlight;
          return (
            <div
              key={opt.value}
              role="option"
              aria-selected={active}
              onMouseEnter={() => setHighlight(idx)}
              onClick={() => onSelect(opt)}
              className={`px-3 py-2 cursor-pointer select-none
                ${active ? "bg-red-50 text-red-600 font-medium" : ""}
                ${hl ? "bg-gray-100" : ""}`}
              style={{ lineHeight: "22px", fontSize: "14px" }}
            >
              {opt.label}
            </div>

          );
        })}
      </div>
    </div>
  ) : null;

  return (
    <>
      <div ref={rootRef} className={`relative ${className}`}>
        <button
          type="button"
          disabled={disabled}
          onClick={() => setOpen((o) => !o)}
          style={triggerStyle}
          className={`w-full border rounded px-3 py-2 text-left flex items-center justify-between focus:ring-2 focus:ring-red-300 ${
            disabled ? "bg-gray-100 cursor-not-allowed" : "bg-white"
          } ${triggerClassName}`}
          style={{ minHeight: "38px", ...triggerStyle }}
        >
          <span className="truncate">
            {selected
              ? renderValue
                ? renderValue(selected)
                : selected.label
              : <span className="text-gray-400">{placeholder}</span>}
          </span>
          {!hideArrow && (
            <span className="ml-2 text-gray-400">▾</span>
          )}

        </button>

        {!menuPortalTarget && dropdownElement}
      </div>
      {menuPortalTarget && dropdownElement && createPortal(dropdownElement, menuPortalTarget)}
    </>
  );
}
